import { TextField, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import DotLegalAutocomplete, { AutocompleteOption } from "../../common/autocomplete/DotLegalAutocomplete";
import Button from "../../common/button/Button";
import DotLegalModal from "../../common/modal/DotLegalModal";
import { Organization } from "../organizations/Organization.types";
import { getOrganizations } from "../organizations/OrganizationService";
import useConfigureUserStyles from "./ConfigureUser.styles";
import { ConfigurableApplicationUser, MenuOption, UserRole } from "./User.types";
import { getCountryOptions, getFilteredMenuOptions, getUserRoles, saveUser } from "./UserService";

const filterWidth = "100%";

export interface AddEditUserDialogProps {
    onClose: () => void;
    user: ConfigurableApplicationUser;
    onSaved: () => void;
}

const userIsEmployee = (user: ConfigurableApplicationUser) => user.userRoles.some((r) => r?.name === "Employee");

function AddEditUserDialog(props: AddEditUserDialogProps) {
    const classes = useConfigureUserStyles();
    const [editableUser, setEditableUser] = useState<ConfigurableApplicationUser>(props.user);
    const [waitingForServer, setWaitingForServer] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [userRoles] = useState<AutocompleteOption<UserRole>[]>(getUserRoles.map((role) => ({ label: role.name, value: role })));
    const [selectedUserRoles, setSelectedUserRoles] = useState<AutocompleteOption<UserRole>[]>(
        editableUser.userRoles.map((role) => ({ label: role.name, value: role }))
    );
    const [organizations, setOrganizations] = useState<AutocompleteOption<Organization>[]>([]);
    const [selectedOrganizations, setSelectedOrganizations] = useState<AutocompleteOption<Organization>[]>(
        editableUser.organizations.map((org) => ({
            label: org.name,
            value: org,
        }))
    );
    const countryOptions = useMemo(
        () =>
            getCountryOptions().map((country) => ({
                label: country.name,
                value: country.code,
            })),
        []
    );

    const [selectedCountries, setSelectedCountries] = useState<AutocompleteOption<string>[]>(() => {
        if (editableUser.countryAccess.length === 0) {
            return countryOptions; // "All" - show all countries selected
        }
        return editableUser.countryAccess.map((code) => {
            const country = getCountryOptions().find((c) => c.code === code);
            return { label: country?.name || code, value: code };
        });
    });

    let filteredMenuOptions = getFilteredMenuOptions(editableUser.userRoles);

    const [menuOptions, setMenuOptions] = useState<AutocompleteOption<MenuOption>[]>(filteredMenuOptions);
    const [selectedMenus, setSelectedMenus] = useState<AutocompleteOption<MenuOption>[]>([]);

    useEffect(() => {
        getOrganizations()
            .then((organizationsFromDb) => setOrganizations(organizationsFromDb.map((org) => ({ label: org.name, value: org }))))
            .catch(() => {});
    }, []);

    function filterSelectedMenuOptions() {
        return editableUser.menuAccess
            .map((menuId) => {
                const menu = filteredMenuOptions.find((m) => m.value.id === menuId);
                return menu ? { label: menu.label, value: menu.value } : null;
            })
            .filter((item) => item !== null) as AutocompleteOption<MenuOption>[];
    }

    // Update menu options when user roles change
    useEffect(() => {
        setMenuOptions(filteredMenuOptions);

        // Update selected menus based on access
        if (editableUser.menuAccess.length === 0) {
            setSelectedMenus(filteredMenuOptions);
        } else {
            setSelectedMenus(filterSelectedMenuOptions());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editableUser.userRoles]);

    const handleSave = (): void => {
        setWaitingForServer(true);
        setErrorMessage(null);
        saveUser(editableUser)
            .then(() => {
                setWaitingForServer(false);
                props.onSaved();
            })
            .catch((reason) => {
                setWaitingForServer(false);
                setErrorMessage(reason?.message || "An error occurred while saving the user");
            });
    };

    function formIsInvalid() {
        return (
            !editableUser.name ||
            !editableUser.email ||
            !editableUser.phoneNumber ||
            !editableUser.countryCode ||
            selectedUserRoles.length === 0 ||
            selectedOrganizations.length === 0 ||
            selectedCountries.length === 0 ||
            selectedMenus.length === 0
        );
    }

    return (
        <DotLegalModal
            open
            onClose={() => {
                props.onClose();
                return undefined;
            }}
            title={editableUser.new ? "Add user" : "Edit user"}
            content={
                <div className={classes.configureUserForm}>
                    <div className={classes.inputContainer}>
                        <TextField
                            variant="outlined"
                            size="small"
                            color="primary"
                            fullWidth
                            label="Name"
                            value={editableUser.name}
                            onChange={(e) => setEditableUser({ ...editableUser, name: e.target.value })}
                            InputLabelProps={{
                                classes: {
                                    root: "",
                                },
                            }}
                        />
                    </div>
                    <div className={classes.inputContainer}>
                        <TextField
                            variant="outlined"
                            size="small"
                            color="primary"
                            fullWidth
                            label="Email"
                            value={editableUser.email}
                            onChange={(e) => setEditableUser({ ...editableUser, email: e.target.value })}
                            InputLabelProps={{
                                classes: {
                                    root: "",
                                },
                            }}
                        />
                    </div>
                    <div className={classes.phoneNumber}>
                        <div style={{ width: 65 }}>
                            <TextField
                                variant="outlined"
                                size="small"
                                color="primary"
                                placeholder="+45"
                                fullWidth
                                value={editableUser.countryCode}
                                onChange={(e) =>
                                    setEditableUser({
                                        ...editableUser,
                                        countryCode: e.target.value,
                                    })
                                }
                                InputLabelProps={{
                                    classes: {
                                        root: "",
                                    },
                                }}
                            />
                        </div>
                        <div>
                            <TextField
                                variant="outlined"
                                size="small"
                                color="primary"
                                label="Phone"
                                fullWidth
                                value={editableUser.phoneNumber}
                                onChange={(e) =>
                                    setEditableUser({
                                        ...editableUser,
                                        phoneNumber: e.target.value,
                                    })
                                }
                                InputLabelProps={{
                                    classes: {
                                        root: "",
                                    },
                                }}
                            />
                        </div>
                    </div>
                    <div className={classes.inputContainer}>
                        <DotLegalAutocomplete
                            onChange={(selectedRoles) => {
                                setEditableUser({
                                    ...editableUser,
                                    userRoles: selectedRoles.filter((r) => r.value !== null).map((r) => r.value) as UserRole[],
                                });
                                setSelectedUserRoles(selectedRoles);
                            }}
                            label="User Role(s)"
                            options={userRoles}
                            value={selectedUserRoles}
                            width={filterWidth}
                            tooltip={`Select the appropriate role(s) for the user. If the user is an external client, choose "Client." If the user is a Bech-Bruun employee, choose "Employee." If the employee requires access to administrative functions, also select "Admin". If the user only needs view-only access, choose "Read only". Please note that users with the "Read only" role cannot view any cost-related information.`}
                        />
                    </div>
                    <div className={classes.inputContainer}>
                        <DotLegalAutocomplete
                            checkbox
                            onChange={(newSelectedOrganizations) => {
                                setEditableUser({
                                    ...editableUser,
                                    organizations: newSelectedOrganizations.filter((org) => org.value !== null).map((org) => org.value) as Organization[],
                                });
                                setSelectedOrganizations(newSelectedOrganizations);
                            }}
                            label="Organisation(s)"
                            options={organizations}
                            selectAllOption={userIsEmployee(editableUser)}
                            value={selectedOrganizations}
                            width={filterWidth}
                            tooltip={`Select the organisation(s) the user should have access to. If the user role "Employee" is selected, you can choose "Select All" at the top of the dropdown. Otherwise, simply select the specific organisation(s) the user should have access to. Be sure to assign access only to relevant organisations.`}
                        />
                    </div>
                    <div className={classes.inputContainer}>
                        <DotLegalAutocomplete
                            checkbox
                            onChange={(newSelectedCountries) => {
                                const countryCodes =
                                    newSelectedCountries.length === countryOptions.length
                                        ? [] // All selected = empty array (all access)
                                        : (newSelectedCountries.filter((c) => c.value !== null).map((c) => c.value) as string[]);

                                setEditableUser({
                                    ...editableUser,
                                    countryAccess: countryCodes,
                                });
                                setSelectedCountries(newSelectedCountries);
                            }}
                            label="Country Access"
                            options={countryOptions}
                            selectAllOption
                            value={selectedCountries}
                            width={filterWidth}
                            tooltip={`Select which countries this user can access. The user will have the same menu permissions across all selected countries.`}
                        />
                        {selectedCountries.length === 0 && (
                            <Typography variant="caption" color="error" style={{ marginTop: 4 }}>
                                At least one country must be selected
                            </Typography>
                        )}
                    </div>
                    <div className={classes.inputContainer}>
                        <DotLegalAutocomplete
                            checkbox
                            onChange={(newSelectedMenus) => {
                                const menuIds =
                                    newSelectedMenus.length === menuOptions.length
                                        ? [] // All selected = empty array (all access)
                                        : newSelectedMenus.filter((m) => m.value !== null).map((m) => m.value!.id);

                                setEditableUser({
                                    ...editableUser,
                                    menuAccess: menuIds,
                                });
                                setSelectedMenus(newSelectedMenus);
                            }}
                            label="Menu Access"
                            options={menuOptions}
                            selectAllOption
                            value={selectedMenus}
                            width={filterWidth}
                            tooltip={`Select which menu sections this user can access. These permissions will apply to all countries the user has access to. For example, if a user has access to "Designs" and both Denmark and Sweden, they can access designs in both countries. You cannot set different menu permissions per country for the same user.`}
                        />
                        {selectedMenus.length === 0 && (
                            <Typography variant="caption" color="error" style={{ marginTop: 4 }}>
                                At least one menu must be selected
                            </Typography>
                        )}
                    </div>
                    {/*{waitingForServer && <CircularProgress />}*/}
                    {errorMessage && (
                        <Typography variant="body2" color="error" style={{ marginTop: 16 }}>
                            {errorMessage}
                        </Typography>
                    )}
                </div>
            }
            action={
                editableUser.new ? (
                    <>
                        <Button buttonType="primary" disabled={formIsInvalid()} onClick={handleSave} isLoading={waitingForServer}>
                            Send invite
                        </Button>
                    </>
                ) : (
                    <Button buttonType="primary" disabled={formIsInvalid()} onClick={handleSave} isLoading={waitingForServer}>
                        Save
                    </Button>
                )
            }
        />
    );
}

export default AddEditUserDialog;
